/**
 * ─── Jewelry Shop Entity Definitions ─────────────────────────
 * Self-registers all jewelry entities with the Entity Registry.
 */

import { EntityRegistry } from '@repo/core'
import type { EntityDefinition } from '@repo/core'
import type {
  JewelryItem, RateCard, SalesOrder, RepairJob, Consignment, Customer,
} from './jewelry.schema'
import { JewelryHooks } from './jewelry.hooks'

export const JewelryItemEntity: EntityDefinition<JewelryItem> = {
  name: 'jewelry_items',
  ui: {
    label: 'Item', labelPlural: 'Inventory', icon: 'Gem',
    routePath: 'jewelry/items', color: 'purple',
    showInNav: true, navOrder: 20, navGroup: 'Jewelry',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'critical' },
  audit: { enabled: true, excludeFields: ['version'] },
  rbac: { enabled: true, permissionPrefix: 'jewelry_item' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

export const RateCardEntity: EntityDefinition<any> = {
  name: 'rate_cards',
  ui: {
    label: 'Rate Card', labelPlural: 'Rate Cards', icon: 'TrendingUp',
    routePath: 'jewelry/rates', color: 'yellow',
    showInNav: true, navOrder: 30, navGroup: 'Jewelry',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'normal' },
  audit: { enabled: true },
  rbac: { enabled: true, permissionPrefix: 'jewelry_rate' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: false },
}

export const SalesOrderEntity: EntityDefinition<SalesOrder> = {
  name: 'sales_orders',
  ui: {
    label: 'Sales Order', labelPlural: 'Sales Orders', icon: 'ShoppingBag',
    routePath: 'jewelry/pos', color: 'green',
    showInNav: true, navOrder: 40, navGroup: 'Jewelry',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'critical' },
  audit: { enabled: true, excludeFields: ['version'] },
  rbac: { enabled: true, permissionPrefix: 'jewelry_order' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: false },
}

export const RepairJobEntity: EntityDefinition<RepairJob> = {
  name: 'repair_jobs',
  ui: {
    label: 'Repair', labelPlural: 'Repairs & Custom', icon: 'Wrench',
    routePath: 'jewelry/repairs', color: 'blue',
    showInNav: true, navOrder: 50, navGroup: 'Jewelry',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'normal' },
  audit: { enabled: true },
  rbac: { enabled: true, permissionPrefix: 'jewelry_repair' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

export const ConsignmentEntity: EntityDefinition<Consignment> = {
  name: 'consignments',
  ui: {
    label: 'Consignment', labelPlural: 'Consignments', icon: 'Handshake',
    routePath: 'jewelry/consignments', color: 'red',
    showInNav: true, navOrder: 60, navGroup: 'Jewelry',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'background' },
  audit: { enabled: true },
  rbac: { enabled: true, permissionPrefix: 'jewelry_consignment' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

export const JewelryCustomerEntity: EntityDefinition<Customer> = {
  name: 'jewelry_customers',
  ui: {
    label: 'Customer', labelPlural: 'Customers', icon: 'Users',
    routePath: 'jewelry/customers', color: 'blue',
    showInNav: true, navOrder: 10, navGroup: 'Jewelry',
  },
  sync: { enabled: true, conflictStrategy: 'lww', priority: 'normal' },
  audit: { enabled: true },
  rbac: { enabled: true, permissionPrefix: 'jewelry_customer' },
  hooks: {},
  pagination: 'offset',
  tenant: { enabled: true, field: 'tenantId' },
  softDelete: { enabled: true, field: 'deletedAt' },
}

// ─── Self-Register ─────────────────────────────────────────

EntityRegistry.register(JewelryItemEntity)
EntityRegistry.register(RateCardEntity)
EntityRegistry.register(SalesOrderEntity)
EntityRegistry.register(RepairJobEntity)
EntityRegistry.register(ConsignmentEntity)
EntityRegistry.register(JewelryCustomerEntity)
